import { ExperimentClient, Experiment, Treatment, Gate } from '@grammarly/experimentation-js-sdk'
import { FacadeService } from '../facade'
import { createExperimentClient } from './client'
import {
  ManakinExperiments,
  StatsigExperiments,
  StatsigTreatmentOrNull,
  TreatmentWithAssignedGroup,
} from './experiments'
import { ClientAssignedExperiments, type ExperimentContext } from './config/client'
import { clientExperimentsConfig } from './config/client-experiments'
import { ServerAssignedExperiments } from './config/server'
import { serverExperimentsConfig } from './config/server-experiments'
import { createExperimentMethods } from './util'
import { Gates, StatsigGates } from './gates'
import { getExperimentOverrides } from './overrides'
import {
  AI_DETECTOR_SLUG,
  PARAPHRASING_TOOL_SLUG,
  GRAMMAR_CHECK_SLUG,
  PLAGIARISM_CHECKER_SLUG,
  AI_HUMANIZER_SLUG,
  WEB_TOOLS_TOP_5_SLUGS,
  PAID_LP_HERO_SLUGS,
} from '../../constants'
import { sanitizeExperimentGroupName } from './util'
import { UserService } from '../user'
import { BrowserService } from '../browser'
import { GeoLocationService } from '../geo-location'
import { getPageSlugFromFacade } from '../../../components/page/utils'
import { isPaidLPGoogleOneTapEligible } from '@components/google-one-tap/eligibility'
import { trackExperimentInfo } from '@lib/tracking/google-tag-manager/events'
import { getSubscriptionType } from '../user/utils'

export type ExperimentKeys = keyof typeof clientExperimentsConfig | keyof typeof serverExperimentsConfig

export type ExperimentAssignmentReturnType<K extends ExperimentKeys> = K extends keyof typeof ClientAssignedExperiments
  ? StatsigTreatmentOrNull<(typeof ClientAssignedExperiments)[K]['experiment']>
  : K extends keyof typeof ServerAssignedExperiments
    ? StatsigTreatmentOrNull<(typeof ServerAssignedExperiments)[K]>
    : never

export type ExperimentMethods = {
  [K in ExperimentKeys as `get${Capitalize<K & string>}Assignment`]: () => ExperimentAssignmentReturnType<K>
} & {
  [K in keyof typeof clientExperimentsConfig as `peek${Capitalize<K & string>}Assignment`]: () => ExperimentAssignmentReturnType<K>
}

export interface ExperimentService extends ExperimentMethods {
  isCompanyAnnouncement6_25Enabled: boolean
  isWebHeartbeatTrackingEnabled: boolean
  isSprigEnabled: boolean
  experimentsWithAssignedTreatment: Map<string, TreatmentWithAssignedGroup>
  gatesWithEnabledGroup: Map<string, TreatmentWithAssignedGroup>
  isMarketingEmailHoldBackEnabled: boolean
  isMarketingEmailControlEnabled: boolean
  isGDPR: boolean
  isUSA: boolean
  isOneTrustTestScript: boolean
  isOneTrustDisabled: boolean
  isAirGapEnabled: boolean
  isAirGapFooterEnabled: boolean
  isFullStoryEnabled: boolean
  isBrowserPagesSignUpFlowEnabled: boolean
  isPageLogFeMetricsEnabled: boolean
  isWhatsNewHeaderItem_25Enabled: boolean
  isUpdateOpeningsPageUrls_25Enabled: boolean
  isSobcWorkweekWithAi_25Enabled: boolean
  isGoogleCtaSignupWallV2: (isAnonymous: boolean) => boolean
  isDynamicBannerV2: (slug: string, isMobileOrTablet: boolean, isAnonymous: boolean) => boolean
  getToolsSecondaryNavEnabled: () => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsSecondaryNav>
  getToolsSecondaryNavLoggedInEnabled: () => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsSecondaryNavLoggedIn>
  getToolsSecondaryNavMobileAssignment: () => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsSecondaryNavMobile>
  getSignedInDesktopEditorPersistentClientCtaAssignment: () => StatsigTreatmentOrNull<
    typeof StatsigExperiments.SignedInDesktopEditorPersistentClientCta
  >
  getSignedInPaidLPDesktopEditorPersistentClientCtaAssignment: () => StatsigTreatmentOrNull<
    typeof StatsigExperiments.SignedInPaidLPHeroCta
  >
  isLocalizedPlansIndiaAnonEnabled: () => boolean
  peekToolsOptimalUsageLimitAssignment: (
    isAnonymous: boolean,
    pageSlug: string
  ) => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsOptimalUsageLimit>
  getToolsOptimalUsageLimitAssignment: (
    isAnonymous: boolean,
    pageSlug: string
  ) => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsOptimalUsageLimit>
  getToolsFreeUseLimitToTrialAssignment: () => StatsigTreatmentOrNull<
    typeof StatsigExperiments.ToolsFreeUseLimitToTrial
  >
  getToolsLtvNavAssignment: () => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsLtvNav>
  isToolsRedesign2_2Enabled: () => boolean
  isToolsRedesign2_2LoggedInEnabled: () => boolean
  isTools2_2WithLoggedInEnabled: () => boolean
  isToolsRedesign2_3AnonEnabled: () => boolean
  isAutomaticSignInEnabled: (isChrome: boolean, isGoogleOneTapEnabled: boolean) => boolean
  getToolsGoogleOneTapAssignment: (
    isMobileOrTablet: boolean
  ) => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsGoogleOneTap>
  assignPagePathValidationAATest: () => void
  isPaidLPWithGoogleOneTap: () => boolean
  getToolPageExitIntentModalAssignment: (
    isAnonymous: boolean
  ) => StatsigTreatmentOrNull<typeof StatsigExperiments.ToolPageExitIntentModal>
  getGlobalNavAnonymousTrialUphookAssignment: () => StatsigTreatmentOrNull<
    typeof StatsigExperiments.GlobalNavAnonymousTrialUphook
  >
}

const isValidTreatment = <EX extends string>(experiment: Experiment<EX>, treatment: string): treatment is EX => {
  return experiment.experiencesArray.includes(treatment as EX)
}

/**
 * Declaration merging: Adds auto-generated experiment method types from config.
 * Methods like getToolsLtvExp3Assignment() are added at runtime via Object.assign in constructor.
 * Manual methods like getAssignment() and peekAssignment() are implemented directly in the class.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface ExperimentsServiceImpl extends ExperimentMethods {
  getAssignment<T extends ExperimentKeys>(key: T): ExperimentAssignmentReturnType<T>
  peekAssignment<T extends ExperimentKeys>(key: T): ExperimentAssignmentReturnType<T>
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class ExperimentsServiceImpl implements ExperimentService {
  private experimentClient: ExperimentClient
  private _experimentsWithAssignedTreatment: Map<string, TreatmentWithAssignedGroup>
  private _experimentsWithPeekedTreatment: Set<string>
  private _gatesWithGroup: Map<string, TreatmentWithAssignedGroup>
  private serverSideAssignTreatments: Treatment[]
  private treatments: Treatment[]
  private userService: UserService
  private geoLocationService: GeoLocationService
  private browserService: BrowserService
  private pageSlug: string
  private isMobileOrTablet: boolean

  constructor(
    fetchFn: typeof window.fetch,
    facade: FacadeService,
    userService: UserService,
    geoLocationService: GeoLocationService,
    browserService: BrowserService
  ) {
    this.experimentClient = createExperimentClient(fetchFn, facade)
    this.userService = userService
    this.geoLocationService = geoLocationService
    this.browserService = browserService
    this.pageSlug = getPageSlugFromFacade(facade)
    this.isMobileOrTablet = browserService.isMobileOrTablet

    this._experimentsWithAssignedTreatment = new Map<string, TreatmentWithAssignedGroup>()
    this._experimentsWithPeekedTreatment = new Set<string>()
    this._gatesWithGroup = new Map<string, TreatmentWithAssignedGroup>()

    this.serverSideAssignTreatments =
      facade.get().serverSideAssignTreatments.map((treatment) => {
        return {
          ...treatment,
          groupName: sanitizeExperimentGroupName(treatment.groupName),
        }
      }) || []

    this.serverSideAssignTreatments.forEach((treatment) => this.addAssignedTreatment(treatment, treatment.groupName))

    this.treatments = facade.get().treatments || []

    // Auto-generate methods from config, but allow manual overrides to take precedence
    const generatedMethods = createExperimentMethods(
      (key) => this.getAssignment(key as ExperimentKeys),
      (key) => this.peekAssignment(key as ExperimentKeys),
      clientExperimentsConfig,
      serverExperimentsConfig
    )

    const methodsToAdd = Object.fromEntries(
      Object.entries(generatedMethods).filter(([methodName]) => !(methodName in this))
    )

    Object.assign(this, methodsToAdd)
  }

  /*
   * Manakin Experiments (deprecated)
   */
  private getOverrideTreatmentManakin = <EX extends string>(experiment: Experiment<EX>): EX | null => {
    const overrides: { [key: string]: string } = IS_CLIENT_SIDE ? getExperimentOverrides() : {}
    const overrideGroup = overrides[experiment.name]
    if (overrideGroup && isValidTreatment(experiment, overrideGroup)) {
      return overrideGroup
    }
    return null
  }

  private getServerSideAssignTreatmentManakin = <EX extends string>(experiment: Experiment<EX>): EX | null => {
    const overrideGroup = this.getOverrideTreatmentManakin(experiment)
    if (overrideGroup) {
      return overrideGroup
    }

    const treatment = this.serverSideAssignTreatments.find((treatment) => experiment.name === treatment.experimentName)
    if (treatment) {
      const treatmentGroup = treatment.groupName

      if (isValidTreatment(experiment, treatmentGroup)) {
        return treatmentGroup
      }
    }

    return null
  }

  // The "Manakin" version of getTreatment does not return "NOT_ASSIGNED". Only use it with experiments set up on Manakin.
  protected getTreatmentManakin: ExperimentClient['getTreatmentManakin'] = <EX extends string>(
    experiment: Experiment<EX>
  ): EX | null => {
    let assignedGroup = this.getOverrideTreatmentManakin(experiment)
    if (!assignedGroup) {
      assignedGroup = this.experimentClient.getTreatmentManakin(experiment)
    }

    if (assignedGroup) {
      const treatmentToAssign = this.treatments.find((treatment) => treatment.experimentName === experiment.name)
      if (treatmentToAssign) {
        this.addAssignedTreatment(treatmentToAssign, assignedGroup)
      }
    }

    return assignedGroup
  }

  // The "Manakin" version of peekTreatment does not return "NOT_ASSIGNED". Only use it with experiments set up on Manakin.
  protected peekTreatmentManakin: ExperimentClient['peekTreatmentManakin'] = <EX extends string>(
    experiment: Experiment<EX>
  ): EX | null => {
    let assignedGroup = this.getOverrideTreatmentManakin(experiment)
    if (!assignedGroup) {
      assignedGroup = this.experimentClient.peekTreatmentManakin(experiment)
    }

    if (assignedGroup) {
      const treatmentToAssign = this.treatments.find((treatment) => treatment.experimentName === experiment.name)
      if (treatmentToAssign) {
        this.addAssignedTreatment(treatmentToAssign, assignedGroup, true)
      }
    }

    return assignedGroup
  }

  /*
   * Statsig Experiments
   */
  private getOverrideTreatment = <EX extends string>(experiment: Experiment<EX>): EX | 'NOT_ASSIGNED' | null => {
    const overrides: { [key: string]: string } = IS_CLIENT_SIDE ? getExperimentOverrides() : {}
    const overrideGroup = overrides[experiment.name]
    if (overrideGroup && isValidTreatment(experiment, overrideGroup)) {
      return overrideGroup
    }
    return null
  }

  private getGateOverrideTreatment = (gate: Gate): string | null => {
    const overrides: { [key: string]: string } = IS_CLIENT_SIDE ? getExperimentOverrides() : {}
    const overrideGroup = overrides[gate.name]
    if (overrideGroup) {
      return overrideGroup
    }
    return null
  }

  private getServerSideAssignTreatment = <EX extends string>(
    experiment: Experiment<EX>
  ): EX | 'NOT_ASSIGNED' | null => {
    const overrideGroup = this.getOverrideTreatment(experiment)
    if (overrideGroup) {
      return overrideGroup
    }

    const treatment = this.serverSideAssignTreatments.find((treatment) => experiment.name === treatment.experimentName)
    if (treatment) {
      const treatmentGroup = treatment.groupName

      if (isValidTreatment(experiment, treatmentGroup)) {
        return treatmentGroup
      }
    }

    return null
  }

  protected getTreatment = <EX extends string>(experiment: Experiment<EX>): EX | null | 'NOT_ASSIGNED' => {
    let assignedGroup = this.getOverrideTreatment(experiment)
    if (!assignedGroup) {
      const treatment = this.experimentClient.getTreatment(experiment)
      assignedGroup = treatment === 'NOT_ASSIGNED' ? null : treatment
    }

    if (assignedGroup) {
      const treatmentToAssign = this.treatments.find((treatment) => treatment.experimentName === experiment.name)
      if (treatmentToAssign) {
        this.addAssignedTreatment(treatmentToAssign, assignedGroup)
      }
    }

    return assignedGroup
  }

  protected peekTreatment: ExperimentClient['peekTreatment'] = (experiment) => {
    let assignedGroup = this.getOverrideTreatment(experiment)
    if (!assignedGroup) {
      assignedGroup = this.experimentClient.peekTreatment(experiment)
    }

    if (assignedGroup) {
      const treatmentToAssign = this.treatments.find((treatment) => treatment.experimentName === experiment.name)
      if (treatmentToAssign) {
        this.addAssignedTreatment(treatmentToAssign, assignedGroup, true)
      }
    }

    return assignedGroup
  }

  protected isGateEnabled: ExperimentClient['isGateEnabled'] = (gate) => {
    const gateOverride = this.getGateOverrideTreatment(gate)
    if (gateOverride) {
      return gateOverride === gate.experience
    }

    return this.experimentClient.isGateEnabled(gate)
  }

  private addAssignedTreatment = (treatment: Treatment, assignedGroup: string, isPeek?: boolean) => {
    if (treatment.type === 'experiment') {
      const isNewAssignment = !this._experimentsWithAssignedTreatment.has(treatment.experimentName)
      const wasPeeked = this._experimentsWithPeekedTreatment.has(treatment.experimentName)
      const assignedTreatment = { ...treatment, assignedGroup }
      this._experimentsWithAssignedTreatment.set(treatment.experimentName, assignedTreatment)

      if (isPeek) {
        this._experimentsWithPeekedTreatment.add(treatment.experimentName)
      } else {
        if (IS_CLIENT_SIDE && (isNewAssignment || wasPeeked)) {
          trackExperimentInfo(assignedTreatment)
          this._experimentsWithPeekedTreatment.delete(treatment.experimentName)
        }
      }
    } else if (treatment.type === 'gate') {
      this._gatesWithGroup.set(treatment.experimentName, { ...treatment, assignedGroup })
    }
  }

  private get experimentContext(): ExperimentContext {
    return {
      userService: this.userService,
      geoLocationService: this.geoLocationService,
      browserService: this.browserService,
      pageSlug: this.pageSlug,
    }
  }

  public getAssignment<T extends ExperimentKeys>(experimentKey: T) {
    if (experimentKey in ClientAssignedExperiments) {
      const config = ClientAssignedExperiments[experimentKey as keyof typeof ClientAssignedExperiments]
      if (!config) {
        return null
      }

      const { experiment, eligibility } = config

      if (!eligibility(this.experimentContext)) {
        return null
      }

      return this.getTreatment(experiment)
    }

    if (experimentKey in ServerAssignedExperiments) {
      const experiment = ServerAssignedExperiments[experimentKey as keyof typeof ServerAssignedExperiments]
      if (!experiment) {
        return null
      }

      return this.getServerSideAssignTreatment(experiment)
    }

    return null
  }

  public peekAssignment<T extends ExperimentKeys>(experimentKey: T) {
    if (!(experimentKey in ClientAssignedExperiments)) {
      return null
    }

    const config = ClientAssignedExperiments[experimentKey as keyof typeof ClientAssignedExperiments]
    if (!config) {
      return null
    }

    const { experiment, eligibility } = config

    if (!eligibility(this.experimentContext)) {
      return null
    }

    return this.peekTreatment(experiment)
  }

  get experimentsWithAssignedTreatment() {
    return this._experimentsWithAssignedTreatment
  }

  get gatesWithEnabledGroup() {
    return this._gatesWithGroup
  }

  get isMarketingEmailHoldBackEnabled() {
    return this.isGateEnabled(Gates.MarketingEmailHoldBack)
  }

  get isMarketingEmailControlEnabled() {
    return this.isGateEnabled(Gates.MarketingEmailControl)
  }

  get isGDPR() {
    return !this.isGateEnabled(Gates.GDPRInverted)
  }

  get isUSA() {
    return this.isGateEnabled(Gates.USA)
  }

  get isOneTrustTestScript() {
    return this.isGateEnabled(Gates.OneTrustTest)
  }

  get isOneTrustDisabled() {
    return this.isGateEnabled(Gates.OneTrustDisabled)
  }

  get isAirGapEnabled() {
    return this.isGateEnabled(Gates.AirGap)
  }

  get isAirGapFooterEnabled() {
    return this.isGateEnabled(Gates.AirGapFooter)
  }

  get isFullStoryEnabled(): boolean {
    return this.isGateEnabled(Gates.FullStoryTracking)
  }

  get isBrowserPagesSignUpFlowEnabled() {
    return this.getServerSideAssignTreatmentManakin(ManakinExperiments.BrowserPagesSignUpFlow) === 'test'
  }

  get isPageLogFeMetricsEnabled() {
    return this.isGateEnabled(Gates.PageLogFeMetrics)
  }

  get isWhatsNewHeaderItem_25Enabled() {
    return this.isGateEnabled(StatsigGates.WhatsNewHeaderItem_25)
  }

  get isUpdateOpeningsPageUrls_25Enabled() {
    return this.isGateEnabled(StatsigGates.UpdateOpeningsPageUrls_25)
  }

  get isSobcWorkweekWithAi_25Enabled() {
    return this.isGateEnabled(StatsigGates.SobcWorkweekWithAi_25)
  }

  get isCompanyAnnouncement6_25Enabled() {
    return this.isGateEnabled(StatsigGates.CompanyAnnouncement6_25)
  }

  get isWebHeartbeatTrackingEnabled() {
    return this.isGateEnabled(StatsigGates.WebHeartbeatTracking)
  }

  get isSprigEnabled(): boolean {
    return this.isGateEnabled(StatsigGates.SprigTracking)
  }

  isLocalizedPlansIndiaAnonEnabled(): boolean {
    const isAnonymous = this.userService.currentUser.isAnonymous
    const isIndia = this.geoLocationService.countryCode === 'IN'

    return isIndia && isAnonymous && this.isGateEnabled(StatsigGates.LocalizedPlansIndiaAnon)
  }

  getToolsSecondaryNavEnabled = () => {
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsSecondaryNav)
  }

  getToolsSecondaryNavLoggedInEnabled = () => {
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsSecondaryNavLoggedIn)
  }

  getToolsSecondaryNavMobileAssignment = () => {
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsSecondaryNavMobile)
  }

  isGoogleCtaSignupWallV2 = (isAnonymous: boolean) => {
    if (isAnonymous) {
      return this.getTreatment(StatsigExperiments.GoogleCtaSignupWallV2) === 'test'
    }
    return false
  }

  isDynamicBannerV2 = (slug: string, isMobileOrTablet: boolean, isAnonymous: boolean) => {
    if ((slug === AI_DETECTOR_SLUG || slug === PARAPHRASING_TOOL_SLUG) && !isMobileOrTablet && isAnonymous) {
      return this.isGateEnabled(StatsigGates.DynamicBannerV2)
    }

    return false
  }

  getSignedInDesktopEditorPersistentClientCtaAssignment = () => {
    return this.getServerSideAssignTreatment(StatsigExperiments.SignedInDesktopEditorPersistentClientCta)
  }

  isEligibleForToolsOptimalValueExchange = (isAnonymous: boolean, pageSlug: string) => {
    const eligiblePageSlugs = [
      AI_DETECTOR_SLUG,
      GRAMMAR_CHECK_SLUG,
      PLAGIARISM_CHECKER_SLUG,
      AI_HUMANIZER_SLUG,
      PARAPHRASING_TOOL_SLUG,
    ]
    return isAnonymous && eligiblePageSlugs.includes(pageSlug)
  }

  peekToolsOptimalUsageLimitAssignment = (isAnonymous: boolean, pageSlug: string) => {
    if (this.isEligibleForToolsOptimalValueExchange(isAnonymous, pageSlug)) {
      return this.peekTreatment(StatsigExperiments.ToolsOptimalUsageLimit)
    }

    return null
  }

  getToolsOptimalUsageLimitAssignment = (isAnonymous: boolean, pageSlug: string) => {
    if (this.isEligibleForToolsOptimalValueExchange(isAnonymous, pageSlug)) {
      return this.getTreatment(StatsigExperiments.ToolsOptimalUsageLimit)
    }

    return null
  }

  isEligibleForToolsFreeUseLimitToTrial = () => {
    const isFreeUser = getSubscriptionType(this.userService.currentUser) === 'free'
    const isNotIndia = this.geoLocationService.countryCode !== 'IN'

    return isFreeUser && isNotIndia && WEB_TOOLS_TOP_5_SLUGS.includes(this.pageSlug)
  }

  getToolsFreeUseLimitToTrialAssignment = () => {
    if (this.isEligibleForToolsFreeUseLimitToTrial()) {
      return this.getTreatment(StatsigExperiments.ToolsFreeUseLimitToTrial)
    }

    return null
  }

  getToolsLtvNavAssignment = () => {
    const isFreeUser = getSubscriptionType(this.userService.currentUser) === 'free'
    const isLoggedIn = !this.userService.currentUser.isAnonymous

    if (isLoggedIn && isFreeUser) {
      return this.getServerSideAssignTreatment(StatsigExperiments.ToolsLtvNav)
    }

    return null
  }

  isEligibleForToolsRedesign2_X = () => {
    const eligiblePageSlugs = [AI_DETECTOR_SLUG, PLAGIARISM_CHECKER_SLUG, AI_HUMANIZER_SLUG, PARAPHRASING_TOOL_SLUG]
    return eligiblePageSlugs.includes(this.pageSlug) && !this.isMobileOrTablet
  }

  isToolsRedesign2_2Enabled = () => {
    if (!this.isEligibleForToolsRedesign2_X()) {
      return false
    }
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsRedesign2_2) === 'test'
  }

  isEligibleForToolsRedesign2_2LoggedIn = () => {
    return this.isEligibleForToolsRedesign2_X() && !this.userService.currentUser.isAnonymous
  }

  isToolsRedesign2_2LoggedInEnabled = () => {
    if (!this.isEligibleForToolsRedesign2_2LoggedIn()) {
      return false
    }
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsRedesign2_2LoggedIn) === 'test'
  }

  isTools2_2WithLoggedInEnabled = () => {
    return this.isToolsRedesign2_2Enabled() || this.isToolsRedesign2_2LoggedInEnabled()
  }

  isToolsRedesign2_3AnonEnabled = () => {
    return this.getServerSideAssignTreatment(StatsigExperiments.ToolsRedesign2_3Anon) === 'test'
  }

  getToolsGoogleOneTapAssignment = (isMobileOrTablet: boolean) => {
    if (
      WEB_TOOLS_TOP_5_SLUGS.includes(this.pageSlug) &&
      !isMobileOrTablet &&
      this.userService.currentUser.isAnonymous
    ) {
      return this.getTreatment(StatsigExperiments.ToolsGoogleOneTap)
    }

    return null
  }

  assignPagePathValidationAATest = () => {
    this.getTreatment(StatsigExperiments.PagePathValidation)
    return
  }

  isPaidLPWithGoogleOneTap = () => {
    const isPaidLP = isPaidLPGoogleOneTapEligible(this.pageSlug)
    if (isPaidLP && this.userService.currentUser.isAnonymous) {
      return this.getTreatment(StatsigExperiments.PaidLPGoogleOneTap) === 'test'
    }
    return false
  }

  getToolPageExitIntentModalAssignment = (isAnonymous: boolean) => {
    if (isAnonymous) {
      return this.getTreatment(StatsigExperiments.ToolPageExitIntentModal)
    }
    return null
  }

  getSignedInPaidLPDesktopEditorPersistentClientCtaAssignment = () => {
    const isPaidLPWithHero = PAID_LP_HERO_SLUGS.includes(this.pageSlug)
    if (isPaidLPWithHero && !this.userService.currentUser.isAnonymous) {
      return this.getServerSideAssignTreatment(StatsigExperiments.SignedInPaidLPHeroCta)
    }
    return null
  }

  isAutomaticSignInEnabled = (isChrome: boolean, isGoogleOneTapEnabled: boolean) => {
    // Google One Tap on Paid landing pages is an experiment, we do not want to mix it with Automatic sign in experiment
    const isPaidLP = isPaidLPGoogleOneTapEligible(this.pageSlug)
    if (isPaidLP) {
      return false
    }

    const isAnonymous = this.userService.currentUser.isAnonymous

    if (!(isAnonymous && isChrome)) {
      return false
    }

    // Is Google One Tap is enabled for this page via Contentful field
    if (!isGoogleOneTapEnabled) {
      return false
    }

    return this.getTreatment(StatsigExperiments.AutomaticSignIn) === 'test'
  }

  getGlobalNavAnonymousTrialUphookAssignment = () => {
    if (this.userService.currentUser.isAnonymous) {
      return this.getTreatment(StatsigExperiments.GlobalNavAnonymousTrialUphook)
    }

    return null
  }
}
